import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import {
  GoogleMap,
  GoogleMapsModule,
  MapDirectionsService
} from '@angular/google-maps';
import { AddressInfo, GeoLocation } from '@moxi.gmbh/moxi-typescriptmodels';
import { RideMapInfo } from '@shared/models';
import { NotificationService } from '@shared/services';
import { hasEmptyProperties } from '@shared/utils';
import { firstValueFrom, map } from 'rxjs';
import { NzSpinModule } from 'ng-zorro-antd/spin';

@Component({
  selector: 'moxi-from-to-map',
  imports: [CommonModule, GoogleMapsModule, NzSpinModule],
  template: ` <nz-spin nzSize="large" [nzSpinning]="isLoading()">
    <div class="google-map">
      @if (isGoogleMapsLoaded()) {
        <google-map
          #map
          width="100%"
          height="100%"
          [zoom]="zoom"
          [options]="options"
        >
          @if (directionsResult()) {
            <map-directions-renderer [directions]="directionsResult()" />
          }
          @for (markerPosition of markerPositions; track $index) {
            <map-marker [position]="markerPosition" />
          }
        </google-map>
      }
    </div>
  </nz-spin>`,
  styles: `
    .google-map {
      height: 500px;
      width: 100%;

      ::ng-deep google-map .map-container {
        border-radius: var(--xl--border-radius);
        border: 1px solid var(--Stroke-Weak);
      }
    }
  `
})
export class FromToMapComponent {
  private readonly directionsService = inject(MapDirectionsService);
  private readonly notificationService = inject(NotificationService);

  from = input.required<AddressInfo | undefined>();
  to = input.required<AddressInfo | undefined>();
  departureTime = input<string>(new Date().toISOString());

  durationChanged = output<RideMapInfo>();

  isLoading = signal(false);
  directionsResult = signal<google.maps.DirectionsResult | undefined>(
    undefined
  );

  mapPanToEffect = effect(() => this.map()?.panTo(this.start));
  locationChangesEffect = effect(() => this.locationChanges());

  markerPositions: google.maps.LatLng[] = [];
  zoom = 7;

  start = this.mapBack({
    latitude: 51.48001404102682,
    longitude: 10.377026441560123
  });

  options: google.maps.MapOptions = {
    disableDefaultUI: true
  };

  map = viewChild<GoogleMap>('map');

  isGoogleMapsLoaded(): boolean {
    return typeof google === 'object' && typeof google.maps === 'object';
  }

  private locationChanges(): void {
    const fromLocation = !hasEmptyProperties(this.from()?.geoLocation)
      ? this.mapBack(this.from().geoLocation)
      : null;
    const toLocation = !hasEmptyProperties(this.to()?.geoLocation)
      ? this.mapBack(this.to().geoLocation)
      : null;

    untracked(() => {
      this.isLoading.set(true);

      if (fromLocation && toLocation) {
        this.getDirections(fromLocation, toLocation);
      } else if (fromLocation) {
        this.gotoLocation(fromLocation);
      } else if (toLocation) {
        this.gotoLocation(toLocation);
      }
    });
  }

  private mapBack(geoLocation: GeoLocation): google.maps.LatLng {
    if (!this.isGoogleMapsLoaded() || !google?.maps?.LatLng) {
      return;
    }

    return geoLocation
      ? new google.maps.LatLng({
          lat: geoLocation.latitude,
          lng: geoLocation.longitude
        } as google.maps.LatLngLiteral)
      : undefined;
  }

  private gotoLocation(location: google.maps.LatLng): void {
    this.markerPositions = [location];
    this.map()?.panTo(location);
    this.zoom = 17;
    this.durationChanged.emit(undefined);
    this.directionsResult.set(undefined);
    this.isLoading.set(false);
  }

  private async getDirections(
    fromLocation: google.maps.LatLng,
    toLocation: google.maps.LatLng
  ): Promise<void> {
    const request: google.maps.DirectionsRequest = {
      destination: toLocation,
      origin: fromLocation,
      travelMode: google.maps.TravelMode.DRIVING,
      drivingOptions: {
        departureTime: this.getValidDepartureDate(this.departureTime()),
        trafficModel: google.maps.TrafficModel.BEST_GUESS
      },
      unitSystem: google?.maps?.UnitSystem?.METRIC ?? 1
    };

    const directionsResult = await firstValueFrom(
      this.directionsService
        .route(request)
        .pipe(map(response => response.result))
    );

    this.directionsResult.set(directionsResult);
    this.markerPositions = [];
    this.isLoading.set(false);

    const route = directionsResult?.routes[0]?.legs[0];
    const rideMapInfo: RideMapInfo = {
      durationWithTraffic: route?.duration_in_traffic?.value,
      durationWithoutTraffic: route?.duration?.value,
      distanceInMeters: route?.distance?.value
    };

    if (hasEmptyProperties(rideMapInfo)) {
      this.notificationService.error('shared.map.rideDurationError');
      return;
    }

    this.durationChanged.emit(rideMapInfo);
  }

  private getValidDepartureDate(date: string): Date {
    return new Date(date) < new Date() ? new Date() : new Date(date);
  }
}
